import html2canvas from 'html2canvas-pro';

/**
 * Captures the visible portion of the main content area (.main-container) as a
 * base64 PNG data URI, suitable for attaching to an AI tool result message.
 *
 * The AI navigation drawer, footer, and navbar are all siblings/overlays of
 * .main-container in the DOM, so they are naturally excluded from the capture.
 *
 * Scale is intentionally reduced (CAPTURE_SCALE) to keep the data URI small and
 * minimise token consumption when sent to the assistant model.
 *
 * Debug mode: set localStorage.setItem('AI_DEBUG_SCREENSHOTS', 'true') in the
 * browser console to trigger an automatic PNG download after each capture, letting
 * you inspect exactly what the model receives.
 *
 * @returns {Promise<string|null>} Base64 PNG data URI, or null if capture fails.
 */

// Capture resolution scale. 1.0 = native screen resolution; increase for sharper text,
// decrease to reduce token consumption when sending to the assistant model.
const CAPTURE_SCALE = 0.6;

export function useScreenshot() {
  /**
   * Captures the .main-container element and returns a base64 PNG data URI.
   * Returns null if the element cannot be found or the capture fails.
   */
  async function captureViewport() {
    const target = document.querySelector('.main-container');
    if (!target) {
      console.warn('[use-screenshot] Could not find .main-container — screenshot aborted.');
      return null;
    }

    try {
      const canvas = await html2canvas(target, {
        scale: CAPTURE_SCALE,
        // Disable logging to keep the console clean
        logging: false,
        // Use CORS-safe image rendering
        useCORS: true,
        // Use the actual visible viewport dimensions so the capture matches what the user sees.
        // scrollWidth/scrollHeight would include off-screen scrollable content, distorting aspect ratio.
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        // Clip the output to the visible height of the element (not the full scrollable height).
        width: target.offsetWidth,
        height: window.innerHeight,
      });

      const dataUri = canvas.toDataURL('image/png');

      // Debug mode: download the screenshot locally so you can inspect what the model sees.
      // Enable via: localStorage.setItem('AI_DEBUG_SCREENSHOTS', 'true')
      if (localStorage.getItem('AI_DEBUG_SCREENSHOTS') === 'true') {
        const link = document.createElement('a');
        link.download = `ai-screenshot-${Date.now()}.png`;
        link.href = dataUri;
        link.click();
        console.info('[use-screenshot] Debug screenshot downloaded.');
      }

      return dataUri;
    } catch (err) {
      console.error('[use-screenshot] Capture failed:', err);
      return null;
    }
  }

  return { captureViewport };
}
